import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import Select from "react-dropdown-select";
import { ImageActions } from "../../_actions";
import { useModal, useApp } from "../../_contexts";
import {
  Button,
  Icon,
  FileDropzone,
  PaneLoader,
  FormError,
} from "../../_components";
import { ErrorMessage } from "@hookform/error-message";
import { toast } from "react-toastify";
import { useEventListener } from "../../_hooks";

const MAX_FILESIZE_MB = 30;

const imageTypeOptions = [
  { value: "cover", icon: "far fa-image-polaroid fa-fw", name: "Cover" },
  { value: "map", icon: "far fa-map fa-fw", name: "Map" },
  // { value: 'image', icon: 'far fa-image fa-fw', name: 'Generic Image' },
];

export function ImageForm({
  item,
  prevModal,
  prevForm,
  defaultType = "cover",
}) {
  // console.log('Image Form Item', item);
  const { data: chronicle } = useSelector((state) => state.chronicle);
  const { details: world } = useSelector((state) => state.world);
  const [isLoading, setLoading] = useState(false);
  const formRef = useRef();

  const { user } = useApp();
  const { openModal, closeModal } = useModal();

  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    setError,
    errors,
    formState,
  } = useForm({
    mode: "all",
    defaultValues: {
      name: item?.name || "",
      imageType: item?.imageType || defaultType,
      images: item ? [item.url] : [],
    },
    shouldUnregister: false,
  });

  const showBackToManager = prevModal && prevModal.length > 0;

  //Handle Form Cancel
  const handleCancel = () => {
    setLoading(false);
    if (prevModal && prevModal.length > 0) {
      if (prevForm) {
        openModal(prevModal, { prevForm });
      } else {
        openModal(prevModal);
      }
    } else {
      closeModal();
    }
  };

  //Handle Select Type Change
  const handleTypeChange = (items) => {
    setValue("imageType", items[0].value);
  };

  //Handle Select Type Display
  const getValue = () => {
    const value = getValues("imageType");
    const item = imageTypeOptions.find((o) => o.value === value);
    return [item];
  };

  //Handle Selected File Change
  // const handleFileChange = (file, onChange) => {
  //   const nameVal = getValues('name');
  //   if (!nameVal || nameVal.length === 0) {
  //     setValue('name', file.path);
  //   }
  //   onChange(file);
  // };

  //Handle Form Submit
  const onSubmit = (formData) => {
    setLoading(true);
    // console.log('ImageForm Data:', formData);
    //Verify if file is a string (URL - Already exists)
    if (!formData.images || formData.images.length === 0) {
      setError("images", "You must select an image");
      toast.error("You must select an image", { autoClose: 4000 });
      console.warn("No images supplied for image upload");
      setLoading(false);
      return;
    }
    //Prepare Form Data
    let imageItem = new FormData();
    imageItem.append("name", formData.name);
    imageItem.append("imageType", formData.imageType);
    imageItem.append("world", world._id);
    imageItem.append("chronicle", chronicle._id);
    imageItem.append("waUser", user.userID);
    if (typeof formData.images[0] === "string") {
      // console.log('No need to update Image.');
      delete formData.images;

      //Send form data...
      if (item && item._id) {
        dispatch(ImageActions.update(item._id, imageItem)).then((result) => {
          // console.log('Update Result', result);
          setLoading(false);
          if (result) {
            if (prevModal && prevModal.length > 0) {
              if (prevForm) {
                openModal(prevModal, { prevForm });
              } else {
                openModal(prevModal);
              }
            } else {
              closeModal();
            }
          } else {
            closeModal();
          }
        });
      }
    } else {
      // //Clear Preview of image (artifact set by FileDropzone)
      delete formData.images[0].preview;

      // const imageItem = {
      //   name: formData.name,
      //   imageType: formData.imageType,
      //   world: world._id,
      //   chronicle: chronicle._id,
      //   waUser: user,
      // };

      imageItem.append("images", formData.images[0]);
      // console.log('final image data to send', imageItem);
      //Check if we're updating an Image record or creating a new one
      if (item && item._id) {
        dispatch(ImageActions.update(item._id, imageItem)).then((result) => {
          // console.log('Update Result', result);
          setLoading(false);
          if (result) {
            if (prevModal && prevModal.length > 0) {
              openModal(prevModal);
            } else {
              closeModal();
            }
          } else {
            closeModal();
          }
        });
      } else {
        //Create new Image record
        dispatch(ImageActions.upload(imageItem)).then((result) => {
          // console.log('Upload Result', result);
          setLoading(false);
          if (result && result.length > 0) {
            if (prevModal && prevModal.length > 0) {
              openModal(prevModal);
            } else {
              closeModal();
            }
          } else {
            //No result was uploaded?
            closeModal();
          }
        });
      }
    }
  };

  const { isValidating, isSubmitting, dirtyFields, touched } = formState;

  const handleKeyClose = (ev) => {
    // console.log("Key up: ", ev);
    if (ev.key && ev.key.toLowerCase() === "escape") {
      console.log("Form State: ", { ...formState });
      if (!isValidating && !isSubmitting) {
        if (
          Object.keys(dirtyFields).length === 0 &&
          Object.keys(touched).length === 0
        ) {
          // console.log("dirty fields!");
          handleCancel();
        }
      }
    }
  };

  useEventListener("keyup", handleKeyClose);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="chronos-form"
      ref={formRef}
    >
      <PaneLoader isLoading={isLoading} isNew text="Image" />
      <div className="form-group">
        {!item && (
          <Controller
            control={control}
            name="images"
            id="images"
            render={({ value, onChange }) => {
              return (
                <FileDropzone
                  onChange={onChange}
                  // fileTypes="image/jpeg,image/png,image/jpg,image/svg,image/vmp"
                  value={value}
                  uploadMb={MAX_FILESIZE_MB}
                  fileTypes="image/jpeg,image/gif,image/png,image/svg+xml,image/webp"
                  className="chronos-file-dropzone"
                />
              );
            }}
          />
        )}
      </div>
      <div className="form-group">
        <label htmlFor="name">Name</label>
        <Controller
          control={control}
          name="name"
          id="name"
          as="input"
          autoComplete="off"
          className="form-control"
          rules={{
            required: {
              value: true,
              message: "You must supply a name for the Image",
            },
            minLength: {
              value: 3,
              message: "The Image name should be 3 or more characters",
            },
            maxLength: {
              value: 250,
              message: "The Image name should be less than 250 characters",
            },
          }}
        />
        <ErrorMessage
          errors={errors}
          name="name"
          render={({ message }) => <FormError message={message} />}
        />
      </div>
      <div className="form-group">
        <label htmlFor="imageType">Image Type</label>
        <Controller
          control={control}
          name="imageType"
          id="imageType"
          //eslint-disable-next-line
          render={({ value, onChange }) => (
            <Select
              options={imageTypeOptions}
              onChange={handleTypeChange}
              values={getValue()}
              valueField="value"
              labelField="name"
              searchField="name"
              searchBy="name"
              className="form-control chronos-select-component"
              dropdownGap={0}
              optionRenderer={({ item }) => (
                <span className="chronos-select-chosen-item" key={item._id}>
                  <Icon icon={item.icon} />
                  &nbsp;{item.name}
                </span>
              )}
              itemRenderer={({ item, itemIndex, methods }) => (
                <div
                  className="chronos-select-dropdown-item"
                  onClick={() => methods.addItem(item)}
                  key={itemIndex}
                >
                  <Icon icon={item.icon} />
                  &nbsp;{item.name}
                </div>
              )}
              contentRenderer={({ state }) => {
                const item = state.values[0] || null;
                return (
                  item && (
                    <div className="chronos-select-chosen-item">
                      <Icon icon={item.icon} />
                      &nbsp;{item.name}
                    </div>
                  )
                );
              }}
            />
          )}
          rules={{
            required: {
              value: true,
              message: "You must specify what kind of Image this is.",
            },
          }}
        />
        <ErrorMessage
          errors={errors}
          name="imageType"
          render={({ message }) => <FormError message={message} />}
        />
      </div>
      <div className="form-controls">
        {showBackToManager && (
          <Button
            color="secondary"
            link
            onClick={handleCancel}
            style={{ fontSize: "0.8rem" }}
          >
            <Icon icon="far fa-chevron-left fa-fw" />
            &nbsp;Go back to Images Manager
          </Button>
        )}
        {!showBackToManager && <React.Fragment>&nbsp;</React.Fragment>}
        {/* &nbsp; */}
        {item && item._id && (
          <Button color="primary" type="submit">
            <Icon icon="far fa-save" />
            &nbsp;Save Image
          </Button>
        )}
        {!item && (
          <Button color="primary" type="submit">
            <Icon icon="fas fa-plus fa-fw" />
            &nbsp;Add Image
          </Button>
        )}
      </div>
      <DevTool control={control} />
    </form>
  );
}
